import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL, formatDateTime } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Check } from "lucide-react";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wsRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items);
      setUnread(r.data.unread_count);
    } catch {}
  };

  useEffect(() => {
    load();
    const token = localStorage.getItem("jastip_token");
    if (!token) return;
    let ws;
    let reconnectTimer;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws/notifications?token=${token}`;
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          setItems((prev) => {
            if (prev.find((i) => i.id === data.id)) return prev;
            return [data, ...prev];
          });
          setUnread((c) => c + 1);
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification(data.title, { body: data.body, icon: "/icons/icon-192.png" });
          }
        } catch {}
      };
      ws.onclose = () => {
        if (cancelled) return;
        // Reconnect after a short backoff if the page is still mounted
        reconnectTimer = setTimeout(connect, 5000);
      };
      ws.onerror = () => {
        // Let onclose handle reconnection
        try { ws.close(); } catch {}
      };
    };
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        try { ws.close(); } catch {}
      }
    };
  }, []);

  const handleClick = async (item) => {
    if (!item.read) {
      await api.post(`/notifications/${item.id}/read`).catch(() => {});
      setUnread((c) => Math.max(0, c - 1));
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: true } : i)));
    }
    if (item.link) navigate(item.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setUnread(0);
    setItems((prev) => prev.map((i) => ({ ...i, read: true })));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative w-9 h-9 border border-zinc-200 bg-white hover:bg-zinc-50 flex items-center justify-center" data-testid="notif-bell-btn">
          <Bell size={14} strokeWidth={1.5} />
          {unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-[#002FA7] text-white text-[10px] font-mono flex items-center justify-center" data-testid="notif-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 sm:w-96 p-0 rounded-none border-zinc-200" align="end" sideOffset={8} data-testid="notif-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
          <div className="label-caps text-zinc-700">Notifications</div>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-[#002FA7] hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
              <Check size={12} strokeWidth={1.5} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="text-center py-12 text-sm text-zinc-400">No notifications yet</div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-4 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors ${!n.read ? "bg-blue-50/30" : ""}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <div className="w-2 h-2 bg-[#002FA7] mt-1.5 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{n.title}</div>
                    <div className="text-xs text-zinc-600 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] font-mono text-zinc-400 mt-1">{formatDateTime(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
